import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import type {
  CompatibilityEntry,
  PanelCatalogEntry,
  Server,
  TopologyPresetEntry,
} from '../api/types.ts';
import { Button, ChoiceGroup, Notice } from '../components/ui.tsx';
import {
  isPanelSupportedForPreset,
  minimumServersFor,
  usableServers,
  type WizardState,
} from './state.ts';

/**
 * Choosing the shape.
 *
 * A shape that needs more servers than the customer has is shown with the
 * number it needs, plus a way to add one — which is more useful than hiding
 * the option and leaving them to wonder where it went.
 */
export function ShapeStep({
  presets,
  panels,
  compatibility,
  servers,
  state,
  onChange,
  onAddServer,
}: {
  presets: readonly TopologyPresetEntry[];
  panels: readonly PanelCatalogEntry[];
  compatibility: readonly CompatibilityEntry[];
  servers: readonly Server[];
  state: WizardState;
  onChange: (state: WizardState) => void;
  onAddServer: () => void;
}): ReactNode {
  const { t, pick } = useI18n();
  const available = usableServers(servers).length;
  const panel = panels.find((entry) => entry.adapterId === state.panelAdapterId);

  /*
   * The shapes this wizard can build at all.
   *
   * It asks which panel to install before it asks where, so an arrangement
   * that installs no panel cannot come from here — the API refuses a panel for
   * one, and a customer who picked it would be walked to a screen that fails.
   * Filtered out rather than shown disabled, because "you cannot choose this
   * here" is not a reason a customer can act on; those are built from the bot,
   * by a wizard that never asks the panel question.
   */
  const offered = presets.filter((preset) => preset.requiresPanel !== false);

  // Of those, the ones this customer could actually pick, given the panel they
  // chose and the servers they have.
  const possible = offered.filter(
    (preset) =>
      minimumServersFor(preset) <= available &&
      (state.panelAdapterId === undefined ||
        isPanelSupportedForPreset(compatibility, state.panelAdapterId, preset.preset)),
  );
  const only = possible.length === 1 ? possible[0] : undefined;

  // One possible answer is not a question. It is still shown, still selected
  // in the list, and still changeable by going back and adding a server — but
  // a customer with one server should not have to choose "single server" from
  // a list of one before they are allowed to continue.
  useEffect(() => {
    if (only && state.preset !== only.preset) {
      onChange({ ...state, preset: only.preset, assignments: {} });
    }
  }, [only, state, onChange]);

  return (
    <>
      {only ? <Notice tone="neutral">{t('wizard.shape.onlyOne')}</Notice> : null}
      <ChoiceGroup
        legend={t('wizard.step.shape')}
        help={t('wizard.shape.help')}
        value={state.preset}
        columns={1}
        onChange={(preset) =>
          // Changing the shape invalidates the slots the previous one had.
          onChange({ ...state, preset, assignments: {} })
        }
        options={offered.map((preset) => {
          const needed = minimumServersFor(preset);
          const notEnough = available < needed;
          const wrongPanel =
            state.panelAdapterId !== undefined &&
            !isPanelSupportedForPreset(compatibility, state.panelAdapterId, preset.preset);
          return {
            value: preset.preset,
            title: pick(preset.name),
            description: pick(preset.summary),
            meta: pick(preset.recommendedFor),
            disabled: notEnough || wrongPanel,
            disabledReason: wrongPanel
              ? compatibility.find(
                  (row) =>
                    row.preset === preset.preset && row.panelAdapterId === state.panelAdapterId,
                )?.reason
                ? pick(
                    compatibility.find(
                      (row) =>
                        row.preset === preset.preset && row.panelAdapterId === state.panelAdapterId,
                    )?.reason,
                  )
                : `${panel?.name ?? ''} — ${t('wizard.panel.unavailableForShape')}`
              : notEnough
                ? t('wizard.shape.needsMoreServers', { count: needed, have: available })
                : undefined,
          };
        })}
      />

      {available === 0 ? (
        <Notice
          tone="warning"
          actions={
            <Button size="sm" onClick={onAddServer}>
              {t('servers.add')}
            </Button>
          }
        >
          {t('wizard.servers.noneReady')}
        </Notice>
      ) : null}
    </>
  );
}
